import React, { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../../api';

const UserFundTransferHistory = () => {
  const [transfers, setTransfers] = useState([]);
  const [summaryStats, setSummaryStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transferType, setTransferType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const fetchTransfers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: currentPage,
        limit: 20,
        transferType
      };
      console.log('Fetching transfers with params:', params);
      const response = await userAPI.getFundTransferHistory(params);
      console.log('API Response:', response);
      
      if (response?.data?.success) {
        setTransfers(response.data.transfers || []);
        setSummaryStats(response.data.summaryStats);
        setPagination(response.data.pagination);
      } else {
        setTransfers([]);
        setSummaryStats(null);
      }
    } catch (err) {
      setError('Failed to load transfer history');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, transferType]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const handleTransferTypeChange = (type) => {
    setTransferType(type);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  console.log('Component State:', { transfers, summaryStats, loading, error });

  if (loading && transfers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#E4572E]"></div>
          <p className="mt-4 text-gray-600">Loading transfer history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-[#0F2F3D] mb-2">Fund Transfer History</h2>
        <p className="text-gray-600">View your fund transfer transactions with team members</p>
      </div>

      {/* Debug Info */}
      {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          Debug Info: Loading={loading.toString()}, Transfers={transfers.length}, Error={error}
        </p>
      </div> */}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Summary Statistics */}
      {summaryStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Transfers</p>
                <p className="text-2xl font-bold text-[#0F2F3D]">{summaryStats.totalTransfers}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-xl">📊</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sent</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(summaryStats.totalSent)}</p>
                <p className="text-xs text-gray-500">{summaryStats.outgoingCount} transactions</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-xl">💸</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Received</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(summaryStats.totalReceived)}</p>
                <p className="text-xs text-gray-500">{summaryStats.incomingCount} transactions</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-xl">💰</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Net Amount</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(summaryStats.totalReceived - summaryStats.totalSent)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-xl">⚖️</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleTransferTypeChange('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              transferType === 'all'
                ? 'bg-[#E4572E] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All ({summaryStats?.totalTransfers || 0})
          </button>
          <button
            onClick={() => handleTransferTypeChange('outgoing')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              transferType === 'outgoing'
                ? 'bg-[#E4572E] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Sent ({summaryStats?.outgoingCount || 0})
          </button>
          <button
            onClick={() => handleTransferTypeChange('incoming')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              transferType === 'incoming'
                ? 'bg-[#E4572E] text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Received ({summaryStats?.incomingCount || 0})
          </button>
        </div>
      </div>

      {/* Transfers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Counterparty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction ID
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transfers.map((transfer, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      transfer.transferType === 'OUTGOING'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {transfer.transferType === 'OUTGOING' ? 'Sent' : 'Received'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${
                      transfer.transferType === 'OUTGOING' ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {transfer.transferType === 'OUTGOING' ? '-' : '+'}
                      {formatCurrency(transfer.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {transfer.transferType === 'OUTGOING' 
                        ? transfer.recipientName 
                        : transfer.senderName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {transfer.transferType === 'OUTGOING' 
                        ? transfer.recipientSponsorCode 
                        : transfer.senderSponsorCode}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(transfer.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-600">
                      {transfer.transactionId}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* No Data */}
      {transfers.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg">No transfer records found</div>
          <div className="text-gray-500 text-sm mt-2">
            {transferType === 'all' 
              ? 'You haven\'t made any fund transfers yet'
              : transferType === 'outgoing'
              ? 'You haven\'t sent any fund transfers yet'
              : 'You haven\'t received any fund transfers yet'
            }
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.currentPage * pagination.limit, pagination.totalTransfers)} of{' '}
            {pagination.totalTransfers} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrev}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFundTransferHistory;
